import React, { useState } from "react";
import axios from "axios";
import { useApp } from "@/context/AppContext";
import { Phone, MessageSquare, Mail, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { externalLinkHandler, externalTarget } from "@/utils/openExternal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE_DISPLAY = "669 024 616";
const PHONE_TEL = "+34669024616";
const EMAIL = "homeatope@gmail.com";
const WHATSAPP_URL =
  "https://wa.me/34669024616?text=" +
  encodeURIComponent("Hola, escribo desde la web de Homeatope.");

export default function Contact() {
  const { t, lang } = useApp();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    topic: "service",
    service_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      setDone(true);
      toast.success(t.contact.successTitle, { description: t.contact.successDesc });
      setForm({
        name: "",
        email: "",
        phone: "",
        topic: "service",
        service_type: "",
        message: "",
      });
    } catch (err) {
      toast.error(t.contact.errorTitle, { description: t.contact.errorDesc });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="ht-section border-t"
      style={{ borderColor: "var(--ht-border)" }}
    >
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-5">
          <p className="ht-eyebrow">{t.contact.eyebrow}</p>
          <h2
            className="mt-3 text-3xl font-bold sm:text-4xl"
            style={{ color: "var(--ht-text)" }}
            data-testid="contact-title"
          >
            {t.contact.title}
          </h2>
          <p className="mt-4 text-base" style={{ color: "var(--ht-text-muted)" }}>
            {t.contact.subtitle}
          </p>

          <div className="mt-8 space-y-3">
            <a
              href={`tel:${PHONE_TEL}`}
              onClick={externalLinkHandler(`tel:${PHONE_TEL}`)}
              data-testid="contact-phone-direct"
              className="ht-card flex items-center gap-3 p-4"
            >
              <span
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg"
                style={{ backgroundColor: "rgba(27,104,178,0.1)", color: "var(--ht-primary)" }}
              >
                <Phone size={18} />
              </span>
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ht-text-muted)" }}>
                  {t.contact.phoneDirect}
                </span>
                <span className="text-base font-semibold" style={{ color: "var(--ht-text)" }}>
                  {PHONE_DISPLAY}
                </span>
              </div>
            </a>
            <a
              href={WHATSAPP_URL}
              target={externalTarget()}
              rel="noopener noreferrer"
              onClick={externalLinkHandler(WHATSAPP_URL)}
              data-testid="contact-whatsapp-cta"
              className="ht-card flex items-center gap-3 p-4"
            >
              <span
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg"
                style={{ backgroundColor: "rgba(34,197,94,0.12)", color: "#22C55E" }}
              >
                <MessageSquare size={18} />
              </span>
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ht-text-muted)" }}>
                  {t.contact.whatsapp}
                </span>
                <span className="text-base font-semibold" style={{ color: "var(--ht-text)" }}>
                  {PHONE_DISPLAY}
                </span>
              </div>
            </a>
            <a
              href={`mailto:${EMAIL}`}
              onClick={externalLinkHandler(`mailto:${EMAIL}`)}
              data-testid="contact-email-link"
              className="ht-card flex items-center gap-3 p-4"
            >
              <span
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg"
                style={{ backgroundColor: "rgba(var(--ht-accent-rgb), 0.12)", color: "var(--ht-accent)" }}
              >
                <Mail size={18} />
              </span>
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ht-text-muted)" }}>
                  Email
                </span>
                <span className="text-base font-semibold" style={{ color: "var(--ht-text)" }}>
                  {EMAIL}
                </span>
              </div>
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="ht-card grid gap-4 p-6 sm:p-8"
          >
            {done && (
              <div
                data-testid="contact-success"
                className="flex items-start gap-3 rounded-md p-3 text-sm"
                style={{
                  backgroundColor: "rgba(34,197,94,0.1)",
                  color: "#16a34a",
                }}
              >
                <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">{t.contact.successTitle}</p>
                  <p className="opacity-80">{t.contact.successDesc}</p>
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t.contact.name} required>
                <input
                  required
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-input-name"
                  className="ht-input"
                />
              </Field>
              <Field label={t.contact.email} required>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-input-email"
                  className="ht-input"
                />
              </Field>
              <Field label={t.contact.phone}>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="contact-input-phone"
                  className="ht-input"
                />
              </Field>
              <Field label={t.contact.topic} required>
                <select
                  value={form.topic}
                  onChange={update("topic")}
                  data-testid="contact-input-topic"
                  className="ht-input"
                >
                  <option value="service">{t.contact.topicService}</option>
                  <option value="course">{t.contact.topicCourse}</option>
                  <option value="other">{t.contact.topicOther}</option>
                </select>
              </Field>
            </div>

            {form.topic === "service" && (
              <Field label={t.contact.serviceType}>
                <select
                  value={form.service_type}
                  onChange={update("service_type")}
                  data-testid="contact-input-service-type"
                  className="ht-input"
                >
                  <option value="">—</option>
                  <option value="urgencias">{t.services.items[0].title}</option>
                  <option value="plumbing">{t.services.items[1].title}</option>
                  <option value="electric">{t.services.items[2].title}</option>
                  <option value="remod">{t.services.items[3].title}</option>
                </select>
              </Field>
            )}

            <Field label={t.contact.msg} required>
              <textarea
                required
                value={form.message}
                onChange={update("message")}
                rows={5}
                data-testid="contact-input-message"
                className="ht-input resize-y"
              />
            </Field>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="ht-btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-60"
              >
                <Send size={16} />
                {submitting ? t.contact.sending : t.contact.send}
              </button>
              {submitting && (
                <span className="text-xs" style={{ color: "var(--ht-text-muted)" }}>
                  <AlertTriangle size={14} className="mr-1 inline" />
                </span>
              )}
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .ht-input {
          width: 100%;
          border: 1px solid var(--ht-border);
          background-color: var(--ht-surface);
          color: var(--ht-text);
          border-radius: 10px;
          padding: 0.65rem 0.9rem;
          font-size: 0.9rem;
          outline: none;
          color-scheme: light dark;
          transition: border-color 180ms ease, box-shadow 180ms ease;
        }
        .ht-input:focus {
          border-color: var(--ht-primary);
          box-shadow: 0 0 0 3px rgba(27, 104, 178, 0.18);
        }
        /* Native <option> elements — force readable colors in dark mode */
        .ht-input option {
          background-color: var(--ht-surface);
          color: var(--ht-text);
        }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--ht-text-muted)" }}>
        {label} {required && <span style={{ color: "var(--ht-accent)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}
